// @migration-in-progress
/**
 * Drill Export Module — PDF generation for safety drill reports  FT-468
 *
 * FT-387 crash-guards PRESERVED throughout:
 *   - (drill.type || '') guards all type accesses
 *   - type-less drills filtered out before processing
 *   - empty / null arrays handled safely
 *
 * exportDrillsReportPDF      → clean A4 table: Date | Drill | Attendees | Duration | Outcome | Notes
 * exportDrillComplianceReportPDF → schedule-vs-actual compliance structure (tidied, NOT flattened)
 * exportIndividualDrillPDF   → unchanged individual drill report
 */

// FIX A (FT-468 Attempt 3): Use the functional autoTable API.
//
// Root cause of "autoTable is not a function": the bundle contains DUPLICATE
// jsPDF module copies (Webpack/CRA code-splitting can produce this). The
// side-effect import `import 'jspdf-autotable'` patches the prototype of ONE
// copy; createPDF() may construct a document from a DIFFERENT copy that was
// never patched. The functional API — autoTable(pdf, options) — operates on
// the passed document object directly, regardless of which prototype was
// patched, making it immune to the duplicate-copy problem.
//
// jspdf-autotable v5.0.2 ESM export:
//   export { autoTable, autoTable as default }
// → `import autoTable from 'jspdf-autotable'` imports the function directly.
import autoTable from 'jspdf-autotable';

import { debug } from '../logging';
import {
  createPDF,
  getPageDimensions,
  setFont,
  checkPageBreak,
  addReportHeader,
  addReportFooter,
  addSectionHeading,
  addWrappedText,
  formatDate,
  formatDateTime,
  savePDF,
  handlePDFError,
  PDF_CONFIG
} from './pdfCore';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * FIX 2 (FT-468): Identify real completion records in the mixed drills array.
 *
 * The IDB drills collection is MIXED: it contains both drill-type DEFINITIONS
 * (fields: drillType, frequencyDays, nextDue, archived/archivedAt; NO `date`)
 * and completion RECORDS (fields: date, type, outcome, duration, notes,
 * participants). It may also contain tombstoned records (deleted_at / deletedAt).
 *
 * A real completion record has `date` AND `type` present; definitions lack
 * `date`. Tombstones are excluded via the caller's filterTombstoned(), but we
 * also guard here defensively (archived === true also excluded).
 *
 * @param {Object} drill - Any item from the drills IDB array
 * @returns {boolean} true only for a real completion record
 */
export function isCompletionRecord(drill) {
  if (!drill || typeof drill !== 'object') return false;
  // Exclude tombstones / archived definitions (defensive belt-and-braces)
  if (drill.deleted_at || drill.deletedAt || drill.archived === true) return false;
  // Definitions have drillType / frequencyDays / nextDue and NO date
  // Completions have date (and usually type + outcome)
  return !!drill.date;
}

/**
 * FIX 3 (FT-468): Format attendees from the correct field.
 *
 * Completion records store attendees in `participants` (not `crew` which is
 * always empty/absent on completions). `participants` may be:
 *   - an Array of name strings  → join with ', '
 *   - a plain number            → "N crew" label
 *   - a plain string            → return as-is
 *   - absent / null             → '—'
 *
 * Legacy data or individual-drill view may have `crew`; fall back to it if
 * `participants` is absent so FT-387 guards still hold.
 *
 * @param {Object} drill - Drill completion record
 * @returns {string} Human-readable attendees string
 */
export function formatParticipants(drill) {
  const p = drill.participants;
  if (p != null) {
    if (Array.isArray(p)) {
      if (p.length === 0) return '—';
      // FIX B (FT-468 Attempt 3): participants may be an array of OBJECTS
      // e.g. [{name:'Billy Sparks'}, ...]. Calling p.join() directly produces
      // '[object Object]'. Extract the display name from each element first.
      const names = p
        .map(item =>
          typeof item === 'string'
            ? item
            : (item?.name || item?.crewName || item?.fullName || item?.displayName || '')
        )
        .filter(Boolean);
      return names.length > 0 ? names.join(', ') : '—';
    }
    if (typeof p === 'number') return `${p} crew`;
    if (typeof p === 'string' && p.trim()) return p;
  }
  // Legacy fallback: some records store crew (array or string)
  const c = drill.crew;
  if (!c) return '—';
  if (Array.isArray(c)) {
    if (c.length === 0) return '—';
    const names = c
      .map(item =>
        typeof item === 'string'
          ? item
          : (item?.name || item?.crewName || item?.fullName || item?.displayName || '')
      )
      .filter(Boolean);
    return names.length > 0 ? names.join(', ') : '—';
  }
  return String(c);
}

/**
 * Map an outcome value to a readable label.
 * Handles both legacy string outcomes (Pass/Fail) and current enum values.
 * FT-387: never throws on undefined/null.
 */
function outcomeLabel(outcome) {
  switch (outcome) {
    case 'Pass':    return 'Pass';
    case 'pass':    return 'Pass';
    case 'success': return 'Pass';
    case 'Fail':    return 'Fail';
    case 'fail':    return 'Fail';
    case 'failed':  return 'Fail';
    case 'partial': return 'Needs Improvement';
    case 'Partial': return 'Needs Improvement';
    default:        return outcome || '—';
  }
}

/**
 * Format crew array (or string) as a readable attendee list.
 * FT-387: crew may be absent or non-array.
 */
function formatAttendees(crew) {
  if (!crew) return '—';
  if (Array.isArray(crew)) {
    return crew.length > 0 ? crew.join(', ') : '—';
  }
  return String(crew);
}

/**
 * Format drill type from snake_case to Title Case.
 * FT-387: guards undefined type.
 */
function formatDrillType(type) {
  return (type || '').replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) || '—';
}

// ---------------------------------------------------------------------------
// 1. DRILLS / HISTORY REPORT — clean table layout (FT-468)
// ---------------------------------------------------------------------------

/**
 * Export drill records as a clean A4 PDF table.
 *
 * Layout:
 *   Minimal header: title + vessel + date range
 *   Plain autoTable: Date | Drill | Attendees | Duration | Outcome | Notes
 *
 * @param {Array}  drills  - Filtered drill records (already type-filtered by the caller)
 * @param {Object} options - { vesselName, category, vesselIMO, reportTitle, drillType }
 *   drillType: 'all' | string (single type) | string[] (multi-type) — used for
 *   header/filename only. The caller (DrillExportModal) pre-filters `drills` via
 *   its own useMemo, so this function does NOT re-filter by type (FT-470 fix).
 * @returns {boolean} Success status
 */
export function exportDrillsReportPDF(drills, options = {}) {
  try {
    const {
      vesselName = 'Unknown Vessel',
      category   = 'Unknown Category',
      vesselIMO  = 'Not Specified',
      reportTitle = 'Safety Drills Report',
      drillType  = 'all'
    } = options;

    if (!drills || drills.length === 0) {
      alert('No drill records found to export.');
      return false;
    }

    // FIX 2 (FT-468): Strip definitions, archived, and deleted items — keep
    // only real completion records so that the 17 definition rows that caused
    // blank PDF rows and a wrong count never reach the export.
    const completions = drills.filter(isCompletionRecord);

    // FT-470 FIX: The caller (DrillExportModal) already filters drills by the
    // selected type(s) via its own useMemo before calling this function.
    // We do NOT re-filter here by drillType because:
    //   (a) single-type: filteredDrills from modal already contains only that type
    //   (b) multi-type: drillType was formerly a joined string ("fire, abandon_ship")
    //       which never matched d.type (a scalar), causing a false zero-match alert
    //   (c) all-types: filteredDrills is already the full completion set
    // The completions array above (after isCompletionRecord strip) IS the export set.
    const filteredDrills = completions;

    if (filteredDrills.length === 0) {
      alert('No drill records found to export.');
      return false;
    }

    const pdf = createPDF();
    const { margin, contentWidth } = getPageDimensions(pdf);

    // --- Minimal header ---
    let currentY = margin;
    currentY = addReportHeader(pdf, currentY, {
      title: reportTitle,
      vesselName,
      category
    });

    // Compact vessel / report metadata line
    // drillType may be: 'all' | single string | array of strings (FT-470)
    setFont(pdf, 'body');
    const metaParts = [`IMO: ${vesselIMO}`, `Drills: ${filteredDrills.length}`];
    if (Array.isArray(drillType)) {
      // Multi-type or normalised array — format each type and join
      const typeLabel = drillType.map(t => formatDrillType(t)).join(', ');
      metaParts.push(`Types: ${typeLabel}`);
    } else if (drillType !== 'all') {
      // Single scalar type (legacy / single-selection path)
      metaParts.push(`Type: ${formatDrillType(drillType)}`);
    }
    // drillType === 'all' → no type label appended (shows all types in rows)
    pdf.text(metaParts.join('   •   '), margin, currentY);
    currentY += PDF_CONFIG.sectionSpacing;

    // --- Clean autoTable ---
    const tableHead = [['Date', 'Drill', 'Attendees', 'Duration (mins)', 'Outcome', 'Notes']];

    const tableBody = filteredDrills.map(drill => [
      formatDate(drill.date),
      formatDrillType(drill.type),         // FT-387: (drill.type || '') via helper
      formatParticipants(drill),           // FIX 3: use participants (not crew) for completions
      drill.duration != null ? `${drill.duration} min` : '—',
      outcomeLabel(drill.outcome),
      drill.notes || '—'
    ]);

    autoTable(pdf, {
      startY: currentY,
      head: tableHead,
      body: tableBody,
      theme: 'striped',
      headStyles: {
        fillColor: [51, 65, 85],   // slate-700 — existing app convention (fleetExport)
        fontSize: PDF_CONFIG.fonts.table.size,
        fontStyle: 'bold',
        textColor: [255, 255, 255]
      },
      bodyStyles: {
        fontSize: PDF_CONFIG.fonts.table.size,
        valign: 'top'
      },
      alternateRowStyles: {
        fillColor: [248, 250, 252]  // slate-50 — subtle stripe
      },
      columnStyles: {
        0: { cellWidth: 22 },   // Date
        1: { cellWidth: 32 },   // Drill
        2: { cellWidth: 40 },   // Attendees
        3: { cellWidth: 18, halign: 'center' }, // Duration
        4: { cellWidth: 22, halign: 'center' }, // Outcome
        5: { cellWidth: 'auto' } // Notes — fills remaining space
      },
      margin: { left: margin, right: margin },
      // Handle page breaks gracefully
      didDrawPage: function () {
        // Nothing extra needed; autoTable handles continuation automatically
      }
    });

    // Footer
    addReportFooter(pdf, {
      customText: 'Safety Drills Register — WBC3 Compliant'
    });

    // Save — build a clean filename suffix from drillType (FT-470: may be array)
    let typeStr = '';
    if (Array.isArray(drillType) && drillType.length > 0) {
      typeStr = '_' + drillType.map(t => t.replace(/\s+/g, '_')).join('_');
    } else if (drillType && drillType !== 'all') {
      typeStr = `_${String(drillType).replace(/\s+/g, '_')}`;
    }
    savePDF(pdf, 'safety_drills_report', new Date(), typeStr);

    debug.log('Drills PDF export completed', {
      drillCount: filteredDrills.length,
      drillType
    });

    return true;

  } catch (error) {
    handlePDFError(error, 'drills report export');
    return false;
  }
}

// ---------------------------------------------------------------------------
// 2. COMPLIANCE REPORT — schedule-vs-actual structure preserved (FT-468)
//    Tidy presentation only: consistent spacing, cleaner column alignment.
//    The meaning (required vs actual, due date, status) is NOT changed.
// ---------------------------------------------------------------------------

/**
 * Export the drill compliance report.
 *
 * Structure (PRESERVED from original — this is a schedule-vs-actual report):
 *   • Expected Drill Schedule table (Drill | Frequency | Status | Next Due)
 *   • Regulatory Requirements & Citations (Mandatory / Recommended split)
 *   • Equipment Dependencies
 *   • Recent Drill Performance (last 10 drills)
 *
 * Only the visual presentation is tidied: tighter spacing, autoTable for the
 * schedule grid, consistent font sizing.
 *
 * @param {Array}  drillRegister - Drill register entries (schedule)
 * @param {Array}  drills        - Completed drill records (actuals)
 * @param {Object} options       - { vesselName, category, vesselIMO, reportTitle, compliancePercentage }
 * @returns {boolean} Success status
 */
export function exportDrillComplianceReportPDF(drillRegister, drills, options = {}) {
  try {
    const {
      vesselName           = 'Unknown Vessel',
      category             = 'Unknown Category',
      vesselIMO            = 'Not Specified',
      reportTitle          = 'Drill Compliance Report',
      compliancePercentage = 0
    } = options;

    if (!drillRegister || drillRegister.length === 0) {
      alert('No drill register found for compliance report.');
      return false;
    }

    const pdf = createPDF();
    const { margin, contentWidth } = getPageDimensions(pdf);

    let currentY = margin;

    // Header
    currentY = addReportHeader(pdf, currentY, {
      title: reportTitle,
      vesselName,
      category
    });

    // Compliance summary line
    setFont(pdf, 'body');
    pdf.text(
      `IMO: ${vesselIMO}   •   Compliance Level: ${compliancePercentage}%   •   Generated: ${formatDate(new Date())}`,
      margin, currentY
    );
    currentY += PDF_CONFIG.sectionSpacing;

    // -----------------------------------------------------------------------
    // SECTION 1: Expected Drill Schedule (autoTable — cleaner than manual cols)
    // -----------------------------------------------------------------------
    currentY = addSectionHeading(pdf, currentY, 'Expected Drill Schedule', 'heading');

    const scheduleHead = [['Drill Type', 'Frequency', 'Status', 'Next Due']];
    const scheduleBody = drillRegister.map(drill => {
      const name = drill.name || (drill.drillType || '').replace(/_/g, ' ') || 'Unknown';
      const frequency = drill.frequencyDays
        ? `Every ${drill.frequencyDays} days`
        : 'As required';
      const isOverdue = drill.nextDue && new Date(drill.nextDue) < new Date();
      const status = drill.status || (isOverdue ? 'Overdue' : 'Current');
      const nextDue = drill.nextDue ? formatDate(drill.nextDue) : 'Not scheduled';
      return [name, frequency, status, nextDue];
    });

    autoTable(pdf, {
      startY: currentY,
      head: scheduleHead,
      body: scheduleBody,
      theme: 'striped',
      headStyles: {
        fillColor: [51, 65, 85],
        fontSize: PDF_CONFIG.fonts.table.size,
        fontStyle: 'bold',
        textColor: [255, 255, 255]
      },
      bodyStyles: {
        fontSize: PDF_CONFIG.fonts.table.size,
        valign: 'top'
      },
      alternateRowStyles: {
        fillColor: [248, 250, 252]
      },
      columnStyles: {
        0: { cellWidth: 55 },
        1: { cellWidth: 35 },
        2: { cellWidth: 25, halign: 'center' },
        3: { cellWidth: 30, halign: 'center' }
      },
      // Colour-code overdue status cells
      didParseCell: function (data) {
        if (data.column.index === 2 && data.section === 'body') {
          const val = (data.cell.raw || '').toLowerCase();
          if (val === 'overdue') {
            data.cell.styles.textColor = [220, 38, 38];  // red-600
            data.cell.styles.fontStyle = 'bold';
          }
        }
      },
      margin: { left: margin, right: margin }
    });

    currentY = pdf.lastAutoTable.finalY + PDF_CONFIG.sectionSpacing;

    // -----------------------------------------------------------------------
    // SECTION 2: Regulatory Requirements & Citations
    // -----------------------------------------------------------------------
    const pageBreak2 = checkPageBreak(pdf, currentY, () => addReportHeader(pdf, margin, {
      title: `${reportTitle} (continued)`, vesselName, category
    }), 40);
    currentY = pageBreak2.currentY;

    currentY = addSectionHeading(pdf, currentY, 'Regulatory Requirements & Citations', 'heading');

    const requiredDrills    = drillRegister.filter(d => d.complianceLevel === 'Required');
    const recommendedDrills = drillRegister.filter(d => d.complianceLevel === 'Recommended');

    if (requiredDrills.length > 0) {
      currentY = addSectionHeading(pdf, currentY, 'Mandatory Drills:', 'subheading');
      requiredDrills.forEach(drill => {
        const pb = checkPageBreak(pdf, currentY, () => addReportHeader(pdf, margin, {
          title: `${reportTitle} (continued)`, vesselName, category
        }), 15);
        currentY = pb.currentY;

        setFont(pdf, 'body');
        pdf.text(`• ${drill.name || drill.drillType || ''}`, margin + 5, currentY);
        currentY += PDF_CONFIG.lineHeight;

        if (drill.regulatorySource) {
          pdf.setFontSize(9);
          pdf.text(`  Citation: ${drill.regulatorySource}`, margin + 10, currentY);
          currentY += PDF_CONFIG.lineHeight;
        }

        if (drill.note || drill.warning) {
          pdf.setFontSize(9);
          pdf.text(`  Note: ${drill.note || drill.warning}`, margin + 10, currentY);
          currentY += PDF_CONFIG.lineHeight;
        }
      });
      currentY += 3;
    }

    if (recommendedDrills.length > 0) {
      currentY = addSectionHeading(pdf, currentY, 'Recommended Drills:', 'subheading');
      recommendedDrills.forEach(drill => {
        const pb = checkPageBreak(pdf, currentY, () => addReportHeader(pdf, margin, {
          title: `${reportTitle} (continued)`, vesselName, category
        }), 12);
        currentY = pb.currentY;

        setFont(pdf, 'body');
        pdf.text(`• ${drill.name || drill.drillType || ''}`, margin + 5, currentY);
        currentY += PDF_CONFIG.lineHeight;

        if (drill.regulatorySource) {
          pdf.setFontSize(9);
          pdf.text(`  Guidance: ${drill.regulatorySource}`, margin + 10, currentY);
          currentY += PDF_CONFIG.lineHeight;
        }
      });
    }

    currentY += PDF_CONFIG.sectionSpacing;

    // -----------------------------------------------------------------------
    // SECTION 3: Equipment Dependencies
    // -----------------------------------------------------------------------
    const pageBreak3 = checkPageBreak(pdf, currentY, () => addReportHeader(pdf, margin, {
      title: `${reportTitle} (continued)`, vesselName, category
    }), 30);
    currentY = pageBreak3.currentY;

    currentY = addSectionHeading(pdf, currentY, 'Equipment Dependencies', 'heading');

    const drillsWithEquipment = drillRegister.filter(d =>
      d.equipmentDescription ||
      (d.linkedEquipmentIds && d.linkedEquipmentIds.length > 0) ||
      (d.linkedEquipmentCategories && d.linkedEquipmentCategories.length > 0)
    );

    if (drillsWithEquipment.length > 0) {
      drillsWithEquipment.forEach(drill => {
        const pb = checkPageBreak(pdf, currentY, () => addReportHeader(pdf, margin, {
          title: `${reportTitle} (continued)`, vesselName, category
        }), 12);
        currentY = pb.currentY;

        setFont(pdf, 'subheading');
        pdf.text(`${drill.name || drill.drillType || ''}:`, margin + 5, currentY);
        currentY += PDF_CONFIG.lineHeight;

        setFont(pdf, 'body');
        if (drill.equipmentDescription) {
          pdf.text(`  Required: ${drill.equipmentDescription}`, margin + 10, currentY);
          currentY += PDF_CONFIG.lineHeight;
        }
        if (drill.linkedEquipmentCategories && drill.linkedEquipmentCategories.length > 0) {
          pdf.text(`  Categories: ${drill.linkedEquipmentCategories.join(', ')}`, margin + 10, currentY);
          currentY += PDF_CONFIG.lineHeight;
        }
        currentY += 2;
      });
    } else {
      setFont(pdf, 'body');
      pdf.text('No specific equipment dependencies documented.', margin + 5, currentY);
      currentY += PDF_CONFIG.lineHeight;
    }

    currentY += PDF_CONFIG.sectionSpacing;

    // -----------------------------------------------------------------------
    // SECTION 4: Recent Drill Performance (actuals — last 10)
    // -----------------------------------------------------------------------
    const pageBreak4 = checkPageBreak(pdf, currentY, () => addReportHeader(pdf, margin, {
      title: `${reportTitle} (continued)`, vesselName, category
    }), 30);
    currentY = pageBreak4.currentY;

    currentY = addSectionHeading(pdf, currentY, 'Recent Drill Performance', 'heading');

    // FIX 2 (FT-468): filter to completions only before slicing for display
    const safeDrills = (Array.isArray(drills) ? drills : []).filter(isCompletionRecord);
    const recentDrills = safeDrills.slice(0, 10);

    if (recentDrills.length > 0) {
      const perfHead = [['Date', 'Drill Type', 'Outcome']];
      const perfBody = recentDrills.map(drill => [
        formatDate(drill.date),
        formatDrillType(drill.type),   // FT-387: (drill.type || '') via helper
        outcomeLabel(drill.outcome)
      ]);

      autoTable(pdf, {
        startY: currentY,
        head: perfHead,
        body: perfBody,
        theme: 'striped',
        headStyles: {
          fillColor: [51, 65, 85],
          fontSize: PDF_CONFIG.fonts.table.size,
          fontStyle: 'bold',
          textColor: [255, 255, 255]
        },
        bodyStyles: {
          fontSize: PDF_CONFIG.fonts.table.size
        },
        alternateRowStyles: {
          fillColor: [248, 250, 252]
        },
        columnStyles: {
          0: { cellWidth: 28 },
          1: { cellWidth: 60 },
          2: { cellWidth: 35, halign: 'center' }
        },
        margin: { left: margin, right: margin }
      });
    } else {
      setFont(pdf, 'body');
      pdf.text('No drill records found.', margin + 5, currentY);
    }

    // Footer
    addReportFooter(pdf, {
      customText: 'Drill Compliance Report — Generated from Drill Register'
    });

    // Save
    savePDF(pdf, 'drill_compliance_report', new Date());

    debug.log('Drill compliance PDF export completed', {
      registerCount: drillRegister.length,
      drillCount: safeDrills.length
    });

    return true;

  } catch (error) {
    handlePDFError(error, 'drill compliance report export');
    return false;
  }
}

// ---------------------------------------------------------------------------
// 3. Individual drill PDF — unchanged (not in FT-468 scope)
// ---------------------------------------------------------------------------

/**
 * Export individual drill as detailed PDF report
 * @param {Object} drill - Drill record
 * @param {Object} options - Export options
 * @returns {boolean} Success status
 */
export function exportIndividualDrillPDF(drill, options = {}) {
  try {
    const {
      vesselName = 'Unknown Vessel',
      category   = 'Unknown Category',
      vesselIMO  = 'Not Specified'
    } = options;

    if (!drill) {
      alert('No drill data provided for export.');
      return false;
    }

    const pdf = createPDF();
    const { margin, contentWidth, pageWidth } = getPageDimensions(pdf);

    let currentY = margin;

    // Header
    setFont(pdf, 'title');
    pdf.text('SAFETY DRILL REPORT', margin, currentY);
    currentY += 10;

    // Vessel details box
    setFont(pdf, 'body');
    pdf.setFillColor(248, 250, 252);
    pdf.rect(margin, currentY, contentWidth, 25, 'F');
    pdf.setDrawColor(226, 232, 240);
    pdf.rect(margin, currentY, contentWidth, 25, 'S');

    currentY += 5;
    pdf.text(`Vessel: ${vesselName}`, margin + 5, currentY);
    currentY += PDF_CONFIG.lineHeight;
    pdf.text(`Category: ${category}`, margin + 5, currentY);
    currentY += PDF_CONFIG.lineHeight;
    pdf.text(`IMO Number: ${vesselIMO}`, margin + 5, currentY);
    currentY += PDF_CONFIG.lineHeight + 10;

    // WBC3 compliance notice
    setFont(pdf, 'small');
    pdf.setFont('helvetica', 'italic');
    pdf.text('This report complies with Workboat Code Edition 3 drill requirements', margin, currentY);
    currentY += PDF_CONFIG.sectionSpacing;

    // Drill type
    setFont(pdf, 'subheading');
    pdf.text(`Drill Type: ${(drill.type || '').replace(/_/g, ' ') || 'Unknown'}`, margin, currentY);  // FT-387
    currentY += PDF_CONFIG.lineHeight + 3;

    // Date and outcome
    setFont(pdf, 'body');
    pdf.text(`Date: ${formatDate(drill.date)}`, margin, currentY);
    currentY += PDF_CONFIG.lineHeight;

    if (drill.outcome) {
      const outcomeColor = drill.outcome === 'Pass'   ? [34, 197, 94]   :
                           drill.outcome === 'Fail'   ? [239, 68, 68]   :
                           [234, 179, 8];
      pdf.setFillColor(...outcomeColor);
      pdf.roundedRect(margin, currentY, 30, 8, 2, 2, 'F');
      pdf.setTextColor(255, 255, 255);
      setFont(pdf, 'small');
      pdf.text(drill.outcome.toUpperCase(), margin + 15, currentY + 5.5, { align: 'center' });
      pdf.setTextColor(0, 0, 0);
      currentY += 12;
    }

    // Separator
    pdf.setLineWidth(0.5);
    pdf.line(margin, currentY, pageWidth - margin, currentY);
    currentY += PDF_CONFIG.sectionSpacing;

    // Crew participation
    if (drill.crew && drill.crew.length > 0) {
      currentY = addSectionHeading(pdf, currentY, 'Crew Participation:', 'subheading');
      setFont(pdf, 'body');
      drill.crew.forEach(crewMember => {
        pdf.text(`  • ${crewMember}`, margin + 5, currentY);
        currentY += PDF_CONFIG.lineHeight;
      });
      currentY += 3;
    }

    // Scenario
    if (drill.scenario) {
      currentY = addSectionHeading(pdf, currentY, 'Scenario:', 'subheading');
      currentY = addWrappedText(pdf, currentY, drill.scenario, { fontType: 'body', indent: 5 });
      currentY += 3;
    }

    // Duration
    if (drill.duration) {
      setFont(pdf, 'body');
      pdf.text(`Duration: ${drill.duration} minutes`, margin, currentY);
      currentY += PDF_CONFIG.lineHeight + 3;
    }

    // Notes
    if (drill.notes) {
      currentY = addSectionHeading(pdf, currentY, 'Notes and Observations:', 'subheading');
      currentY = addWrappedText(pdf, currentY, drill.notes, { fontType: 'body', indent: 5 });
    }

    // Footer
    addReportFooter(pdf);

    // Filename
    const dateStr = formatDate(drill.date).replace(/\//g, '-');
    const drillType = (drill.type || 'unknown').replace(/\s+/g, '_').toUpperCase();  // FT-387
    savePDF(pdf, `${drillType}_Drill`, drill.date, dateStr);

    debug.log('Individual drill PDF exported', {
      drillType: drill.type,
      drillDate: drill.date
    });

    return true;

  } catch (error) {
    handlePDFError(error, 'individual drill export', 'Failed to generate drill report. Please try again.');
    return false;
  }
}
